import React, { useEffect, useState } from "react";
import TextBlockLoader from "../loaders/TextBlockLoader";

export default function RecipeIngredients({ ingredients, loading }) {
  const [checkedIngredients, setCheckedIngredients] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);

  useEffect(() => {
    if (ingredients) {
      setExpandedCategories(Object.keys(ingredients)); // Automatically expand all categories
    }
  }, [ingredients])

  const toggleIngredient = (ingredient) => {
    setCheckedIngredients((prev) =>
      prev.includes(ingredient)
        ? prev.filter((item) => item !== ingredient)
        : [...prev, ingredient]
    );
  };

  const ingredientCategories = {
    produce: "🥬 Produce",
    proteins: "🥩 Proteins",
    dairy: "🧀 Dairy",
    spices_and_seasonings: "🧂 Spices & seasonings",
    grains_and_starches: "🍞 Grains & starches",
    other: "🍽 Other",
    optional: "✨ Optional",
  };

  const highlightIngredientText = (text) => {
    const quantityRegex = /^\s*((?:(?:\d+\/\d+)|(?:\d+(?:\.\d+)?(?:\s+\d+\/\d+|[¼½¾⅛⅜⅝⅞])?)|[¼½¾⅛⅜⅝⅞]|(?:one|two|three|four|five|six|seven|eight|nine|ten))(?:\s*(?:-|and|to)\s*(?:(?:\d+\/\d+)|(?:\d+(?:\.\d+)?(?:\s+\d+\/\d+|[¼½¾⅛⅜⅝⅞])?)|[¼½¾⅛⅜⅝⅞]|(?:one|two|three|four|five|six|seven|eight|nine|ten)))?\s*(?:(?:teaspoons?|tsp|tablespoons?|tbsp|cups?|cup|oz|ounces?|lbs?|pounds?|grams?|g|ml|liters?|l|cans?|packets?|cloves?|slices?|pieces?))?)\b/igmu;

    return text
      .replace(
        quantityRegex,
        (match) =>
          `<strong class="font-semibold text-gray-700">${match}</strong>`
      )
      .split(/(<strong.*?<\/strong>)/)
      .map((part, i) =>
        part.startsWith("<strong") ? (
          <span key={i} dangerouslySetInnerHTML={{ __html: part }} />
        ) : (
          part
        )
      );
  };

  return (
    <div>
      {/* Expand/Collapse Controls */}
      <div className="flex items-center justify-between mb-2 mt-6">
        <h3 className="text-xl font-semibold text-gray-800">Ingredients</h3>
        {ingredients && Object.keys(ingredients).length > 0 && (
          <div className="space-x-4">
            <button
              className="text-sm text-green-600 hover:underline"
              onClick={() => setExpandedCategories(Object.keys(ingredients))}
            >
              <span className="inline sm:hidden">Expand</span>
              <span className="hidden sm:inline">Expand all</span>
            </button>
            <button
              className="text-sm text-green-600 hover:underline"
              onClick={() => setExpandedCategories([])}
            >
              <span className="inline sm:hidden">Collapse</span>
              <span className="hidden sm:inline">Collapse all</span>
            </button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <TextBlockLoader />
      ) : ingredients && Object.keys(ingredients).length > 0 ? (
        <div className="space-y-4 bg-white border border-gray-300 rounded-lg p-4 shadow-sm">
          {Object.entries(ingredients)
            .filter(([_, items]) => items.length > 0)
            .map(([category, items]) => {
              const isExpanded = expandedCategories.includes(category);

              return (
                <div key={category}>
                  {/* Category Header */}
                  <div
                    className="flex justify-between items-center cursor-pointer text-gray-700 mb-2"
                    onClick={() =>
                      setExpandedCategories((prev) =>
                        isExpanded
                          ? prev.filter((c) => c !== category)
                          : [...prev, category]
                      )
                    }
                  >
                    <h4 className="text-md font-semibold">
                      {ingredientCategories[category] || category}
                    </h4>
                    <span className="text-gray-500 font-semibold transition hover:font-bold">
                      {isExpanded ? "-" : "+"}
                    </span>
                  </div>

                  {/* Ingredients List */}
                  {isExpanded && (
                    <ul className="space-y-2">
                      {items.map((ingredient, index) => (
                        <li key={index} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={checkedIngredients.includes(ingredient)}
                            onChange={() => toggleIngredient(ingredient)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span
                            className={`ml-2 text-sm ${checkedIngredients.includes(ingredient) ? "line-through text-gray-400" : ""
                              }`}
                          >
                            {highlightIngredientText(ingredient)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
        </div>
      ) : (
        <p className="text-gray-500">No ingredients available.</p>
      )}
    </div>
  );
}
